import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, CheckCircle2, TrendingUp, FileText, ArrowLeft, Download, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const AssessmentResults = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [call, setCall] = useState(null);
  const [sop, setSop] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssessment();
    fetchCall();
  }, [callId]);

  const fetchAssessment = async () => {
    try {
      const response = await axios.get(`${API}/assessments/${callId}`);
      setAssessment(response.data);
      
      // Fetch SOP details
      if (response.data.sop_id) {
        const sopResponse = await axios.get(`${API}/sops/${response.data.sop_id}`);
        setSop(sopResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCall = async () => {
    try {
      const response = await axios.get(`${API}/calls/${callId}`);
      setCall(response.data);
    } catch (error) {
      console.error('Failed to fetch call:', error);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-rose-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-emerald-50 border-emerald-200';
    if (score >= 60) return 'bg-amber-50 border-amber-200';
    return 'bg-rose-50 border-rose-200';
  };

  const handleExportPDF = async () => {
    try {
      const resp = await axios.get(`${API}/assessments/${callId}/export-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `audit_${callId.slice(0, 8)}.pdf`; a.click();
      toast.success('PDF downloaded!');
    } catch {
      toast.error('PDF export failed');
    }
  };

  const handleExportCSV = async () => {
    try {
      const resp = await axios.get(`${API}/assessments/${callId}/export-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `audit_${callId.slice(0, 8)}.csv`; a.click();
      toast.success('CSV downloaded!');
    } catch {
      toast.error('CSV export failed');
    }
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      high: 'bg-rose-50 text-rose-700 border-rose-200',
      medium: 'bg-amber-50 text-amber-700 border-amber-200',
      low: 'bg-blue-50 text-blue-700 border-blue-200',
    };
    return badges[severity] || badges.medium;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <p className="text-slate-600">Loading assessment...</p>
        </div>
      </Layout>
    );
  }

  if (!assessment) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-600 mb-4">Assessment not found</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 fade-in" data-testid="assessment-results-page">
        {/* Header */}
        <div>
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="back-button">
            <ArrowLeft size={16} className="mr-2" />Back
          </Button>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Assessment Results
              </h1>
              <p className="text-slate-600">{call?.title || 'Call Assessment'}</p>
            </div>
            {/* Export buttons */}
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-button"
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                <FileDown size={15} className="mr-1.5" />Download PDF
              </Button>
              <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-button"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                <Download size={15} className="mr-1.5" />Download CSV
              </Button>
            </div>
          </div>
        </div>

        {/* Overall Score */}
        <Card className={`p-8 border ${getScoreBgColor(assessment.overall_score)} shadow-sm`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 mb-2">Overall Compliance Score</p>
              <p className={`text-6xl font-bold ${getScoreColor(assessment.overall_score)}`} data-testid="overall-score">
                {assessment.overall_score}%
              </p>
              <p className="text-sm text-slate-600 mt-2">
                Assessed on {new Date(assessment.assessed_at).toLocaleDateString()}
              </p>
            </div>
            <TrendingUp size={64} className={getScoreColor(assessment.overall_score)} />
          </div>
        </Card>

        {/* Section Scores */}
        <Card className="p-6 border border-slate-200 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Section Breakdown
          </h2>
          <div className="space-y-4">
            {Object.entries(assessment.section_scores || {}).map(([section, score]) => {
              // Find weight from SOP parameters
              const paramWeight = sop?.parameters?.find(
                (p) => p.name.toLowerCase().replace(' ', '_') === section.toLowerCase()
              )?.weight;
              
              return (
                <div key={section} data-testid={`section-score-${section}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900 capitalize">
                        {section.replace(/_/g, ' ')}
                      </p>
                      {paramWeight && (
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                          Weight: {paramWeight}%
                        </span>
                      )}
                    </div>
                    <p className={`font-bold ${getScoreColor(score)}`}>{score}%</p>
                  </div>
                  <Progress value={score} className="h-2" />
                </div>
              );
            })}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Violations */}
          <Card className="p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle size={24} className="text-rose-600" />
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Violations
              </h2>
            </div>
            {assessment.violations && assessment.violations.length > 0 ? (
              <div className="space-y-3">
                {assessment.violations.map((violation, index) => (
                  <div
                    key={index}
                    className="p-4 border border-slate-200 rounded-lg"
                    data-testid={`violation-${index}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium text-slate-900 capitalize">{violation.section.replace(/_/g, ' ')}</p>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityBadge(
                          violation.severity
                        )}`}
                      >
                        {violation.severity}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{violation.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
                <p className="text-slate-600">No violations found</p>
              </div>
            )}
          </Card>

          {/* Recommendations */}
          <Card className="p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={24} className="text-indigo-600" />
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Recommendations
              </h2>
            </div>
            {assessment.recommendations && assessment.recommendations.length > 0 ? (
              <div className="space-y-3">
                {assessment.recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className="flex gap-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg"
                    data-testid={`recommendation-${index}`}
                  >
                    <CheckCircle2 size={20} className="text-indigo-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-700">{rec}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-600">No recommendations available</p>
            )}
          </Card>
        </div>

        {/* Transcript */}
        <Card className="p-6 border border-slate-200 shadow-sm">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Call Transcript
          </h2>
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg max-h-96 overflow-y-auto">
            <p
              className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              data-testid="transcript-content"
            >
              {assessment.transcript}
            </p>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AssessmentResults;